import { Trophy, Zap, Shield } from "lucide-react";
import { useEffect, useRef, useState } from "react";

const AboutSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  return (
    <section ref={sectionRef} className="py-20 bg-gradient-to-b from-white to-gray-50 relative overflow-hidden">
      {/* Background decorative elements */}
      <div className="absolute top-20 left-10 w-72 h-72 bg-blue-100 rounded-full opacity-20 blur-3xl"></div>
      <div className="absolute bottom-20 right-10 w-96 h-96 bg-purple-100 rounded-full opacity-20 blur-3xl"></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className={`text-center mb-16 transition-all duration-1000 transform ${
          isVisible ? 'translate-y-0 opacity-100' : 'translate-y-12 opacity-0'
        }`}>
          <div className="inline-flex items-center space-x-2 bg-blue-100 px-4 py-2 rounded-full mb-4">
            <span className="text-blue-600 font-semibold text-sm">POR QUÉ ELEGIRNOS</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Somos la plataforma líder en México para gestionar todos tus trámites
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Te llevamos todos tus trámites gubernamentales y oficiales de manera rápida, segura y profesional.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {[
            {
              icon: Trophy,
              title: "Experiencia Comprobada",
              description: "Más de 10 años gestionando trámites con 95% de tasa de éxito",
              gradient: "from-yellow-400 to-orange-500",
              delay: "0ms"
            },
            {
              icon: Zap,
              title: "Proceso Ultra Rápido",
              description: "Reducimos tiempos de espera hasta en un 70% comparado con procesos tradicionales",
              gradient: "from-blue-400 to-cyan-500",
              delay: "200ms"
            },
            {
              icon: Shield,
              title: "100% Seguro y Confiable",
              description: "Protección de datos con certificación SSL y cumplimiento de normativas mexicanas",
              gradient: "from-purple-400 to-pink-500",
              delay: "400ms"
            }
          ].map((feature, idx) => (
            <FeatureCard
              key={idx}
              {...feature}
              isVisible={isVisible}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

const FeatureCard = ({ icon: Icon, title, description, gradient, delay, isVisible }) => (
  <div
    className={`group relative bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 border border-gray-100 overflow-hidden ${
      isVisible ? 'translate-y-0 opacity-100' : 'translate-y-12 opacity-0'
    }`}
    style={{ transitionDelay: delay }}
  >
    <div className={`absolute inset-0 bg-gradient-to-br ${gradient} opacity-0 group-hover:opacity-5 transition-opacity duration-500`}></div>

    <div className="relative z-10">
      <div className={`w-16 h-16 bg-gradient-to-br ${gradient} rounded-2xl flex items-center justify-center mb-6 transform group-hover:scale-110 group-hover:rotate-6 transition-all duration-500 shadow-lg`}>
        <Icon className="w-8 h-8 text-white" />
      </div>

      <h3 className="text-lg font-bold text-gray-900 mb-4 group-hover:text-transparent group-hover:bg-gradient-to-r group-hover:bg-clip-text group-hover:from-blue-600 group-hover:to-purple-600 transition-all duration-300">
        {title}
      </h3>
      <p className="text-gray-600 leading-relaxed">{description}</p>
    </div>

    <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-blue-500/10 to-purple-500/10 rounded-bl-full opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
  </div>
);

export default AboutSection;